import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import GeniLoader from "../components/GeniLoader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { useLanguage } from "../context/LanguageContext";
import { toast } from "sonner";
import {
  UploadSimple,
  CheckCircle,
  WarningCircle,
  Info,
  FileXls,
  ArrowsClockwise,
  ChartBar,
  ArrowRight,
  ClockCounterClockwise,
} from "phosphor-react";

const EXCHANGE_LOGOS = {
  binance: "/assets/exchanges/binance.png",
  bitget: "/assets/exchanges/bitget.png",
  lbank: "/assets/exchanges/lbank.png",
  mexc: "/assets/exchanges/mexc.png",
  bingx: "/assets/exchanges/bingx.png",
};

const ExchangeLogo = ({ exchange, className = "w-5 h-5" }) => {
  const src = EXCHANGE_LOGOS[exchange];
  if (!src) return null;
  return (
    <img
      src={src}
      alt={`${exchange} logo`}
      className={`${className} rounded-sm object-contain flex-shrink-0`}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      data-testid={`import-exchange-logo-${exchange}`}
    />
  );
};

export default function ImportExchangeData() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const tradesSectionRef = useRef(null);

  const [selectedExchange, setSelectedExchange] = useState("lbank");
  const [uploading, setUploading] = useState(false);
  const [importSummary, setImportSummary] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const [trades, setTrades] = useState([]);
  const [loadingTrades, setLoadingTrades] = useState(true);

  // Complete modal state
  const [completeTrade, setCompleteTrade] = useState(null);
  const [setups, setSetups] = useState([]);
  const [cSetup, setCSetup] = useState("");
  const [cTimeframe, setCTimeframe] = useState("");
  const [cHandSize, setCHandSize] = useState("");
  const [completing, setCompleting] = useState(false);

  const t = {
    ptBR: {
      title: "IMPORT EXCHANGE DATA",
      subtitle: "Importe seus trades históricos exportados diretamente da sua corretora.",
      selectExchange: "Selecione a corretora",
      comingSoon: "Import support coming soon",
      formatAccepted: "Formato aceito",
      history: "Disponibilidade histórica",
      uploadTitle: "Enviar arquivo",
      uploadHint: "Arraste o arquivo .xlsx aqui ou clique para selecionar",
      uploadHintExchange: "Somente arquivos .xlsx exportados oficialmente pela LBank Futures.",
      howToExport: "Como exportar",
      uploading: "Importando...",
      importCompleted: "Import completed",
      execImported: "Execuções importadas",
      dupIgnored: "Duplicados ignorados",
      invalidRows: "Linhas inválidas",
      tradesRecognized: "Operações reconhecidas",
      tradesPending: "Operações pendentes de complementação",
      viewTrades: "Ver Pending Trades",
      pendingHint: "Complete os trades importados na fila central (API + Import).",
      tradesToComplete: "OPERAÇÕES PARA COMPLETAR",
      noTrades: "Nenhuma operação importada pendente. Envie um arquivo para começar.",
      imported: "Importado",
      complete: "Completo",
      completeTrade: "Completar Trade",
      direction: "Direção",
      date: "Data",
      status: "Status",
      result: "Resultado",
      completeAndSend: "Completar e enviar ao Analytics",
      saving: "Salvando...",
      fillFields: "Preencha Setup, Timeframe e Hand Size.",
      completed: "Trade completado e enviado ao Analytics.",
      onlyXlsx: "Envie um arquivo .xlsx válido.",
      howToTitle: "Como exportar o histórico da LBank Futures",
      howSteps: [
        "Acesse a LBank Futures e abra Orders / Histórico de transações (Transaction History).",
        "Selecione o período desejado (a LBank permite exportar por intervalo de datas).",
        "Clique em Export / Exportar e escolha o formato XLSX.",
        "Faça o upload do arquivo .xlsx exportado aqui no GENI.",
      ],
      select: "Selecione...",
    },
    en: {
      title: "IMPORT EXCHANGE DATA",
      subtitle: "Import your historical trades exported directly from your exchange.",
      selectExchange: "Select exchange",
      comingSoon: "Import support coming soon",
      formatAccepted: "Accepted format",
      history: "Historical availability",
      uploadTitle: "Upload file",
      uploadHint: "Drag your .xlsx file here or click to select",
      uploadHintExchange: "Only .xlsx files officially exported by LBank Futures.",
      howToExport: "How to export",
      uploading: "Importing...",
      importCompleted: "Import completed",
      execImported: "Executions imported",
      dupIgnored: "Duplicates ignored",
      invalidRows: "Invalid rows",
      tradesRecognized: "Trades recognized",
      tradesPending: "Trades pending completion",
      viewTrades: "View Pending Trades",
      pendingHint: "Complete your imported trades in the central queue (API + Import).",
      tradesToComplete: "TRADES TO COMPLETE",
      noTrades: "No pending imported trades. Upload a file to get started.",
      imported: "Imported",
      complete: "Complete",
      completeTrade: "Complete Trade",
      direction: "Direction",
      date: "Date",
      status: "Status",
      result: "Result",
      completeAndSend: "Complete & send to Analytics",
      saving: "Saving...",
      fillFields: "Fill Setup, Timeframe and Hand Size.",
      completed: "Trade completed and sent to Analytics.",
      onlyXlsx: "Please upload a valid .xlsx file.",
      howToTitle: "How to export your LBank Futures history",
      howSteps: [
        "Go to LBank Futures and open Orders / Transaction History.",
        "Select the desired period (LBank lets you export by date range).",
        "Click Export and choose the XLSX format.",
        "Upload the exported .xlsx file here in GENI.",
      ],
      select: "Select...",
    },
  }[language === "ptBR" ? "ptBR" : "en"];

  const lang = language === "ptBR" ? "ptBR" : "en";

  const EXCHANGES = [
    {
      id: "binance", name: "BINANCE", enabled: true, format: "CSV",
      hist: { ptBR: "Período disponibilizado pela Binance (Trade History, USDⓈ-M).", en: "Period provided by Binance (Trade History, USDⓈ-M)." },
      guide: {
        ptBR: {
          hintExchange: "Somente arquivos .csv exportados oficialmente pela Binance (Trade History, USDⓈ-M).",
          howToTitle: "Como exportar o histórico da Binance Futures",
          howSteps: [
            "Entre na Binance.",
            "Vá em Orders.",
            "Abra Futures Order.",
            "Selecione Trade History.",
            "Abra a Central de Download/Export.",
            "Em Tipo de Dados selecione: Futures - Histórico de Trades (use Trade History, NÃO Order History).",
            "Em Tipo selecione: USDⓈ-M Futures.",
            "Escolha o período desejado (a tela permite até 12 meses por exportação; para períodos maiores, gere arquivos separados e importe cada um).",
            "Selecione o formato CSV (recomendado/aceito pelo GENI).",
            "Clique em Gerar.",
            "Quando o arquivo estiver disponível, faça o download.",
            "Envie o CSV original aqui no GENI, sem editar ou converter.",
          ],
        },
        en: {
          hintExchange: "Only .csv files officially exported by Binance (Trade History, USDⓈ-M).",
          howToTitle: "How to export your Binance Futures history",
          howSteps: [
            "Log in to Binance.",
            "Go to Orders.",
            "Open Futures Order.",
            "Select Trade History.",
            "Open the Download/Export Center.",
            "In Data Type select: Futures - Trade History (use Trade History, NOT Order History).",
            "In Type select: USDⓈ-M Futures.",
            "Pick the desired period (the screen allows up to 12 months per export; for longer ranges, generate separate files and import each one).",
            "Select the CSV format (recommended/accepted by GENI).",
            "Click Generate.",
            "Once the file is ready, download it.",
            "Upload the original CSV here in GENI, without editing or converting it.",
          ],
        },
      },
    },
    {
      id: "bitget", name: "BITGET", enabled: true, format: "CSV",
      hist: { ptBR: "Histórico de ordens de Futuros USDⓈ-M (CSV).", en: "USDⓈ-M Futures order history (CSV)." },
      guide: {
        ptBR: {
          hintExchange: "Somente arquivos .csv de Histórico de ordens de Futuros USDⓈ-M exportados pela Bitget.",
          howToTitle: "Como exportar o histórico da Bitget Futures",
          howSteps: [
            "Entre na Bitget.",
            "Vá em Orders / Ordens.",
            "Abra Futures Orders / Ordens de futuros.",
            "Acesse Exportação de dados.",
            "Selecione o histórico de Futuros correspondente (USDⓈ-M — Histórico de ordens).",
            "Escolha o período desejado.",
            "Clique em Gerar / Exportar.",
            "Quando o arquivo estiver disponível, faça o download do CSV.",
            "Envie o CSV original aqui no GENI, sem editar ou converter.",
          ],
        },
        en: {
          hintExchange: "Only .csv USDⓈ-M Futures order-history files exported by Bitget.",
          howToTitle: "How to export your Bitget Futures history",
          howSteps: [
            "Log in to Bitget.",
            "Go to Orders.",
            "Open Futures Orders.",
            "Open Data Export.",
            "Select the matching Futures history (USDⓈ-M — Order history).",
            "Pick the desired period.",
            "Click Generate / Export.",
            "Once the file is ready, download the CSV.",
            "Upload the original CSV here in GENI, without editing or converting it.",
          ],
        },
      },
    },
    {
      id: "bingx", name: "BINGX", enabled: true, format: "CSV",
      hist: { ptBR: "Standard Futures — cada linha é uma posição fechada (CSV).", en: "Standard Futures — each row is a closed position (CSV)." },
      guide: {
        ptBR: {
          hintExchange: "Somente arquivos .csv de Standard Futures (Standard_Futures.csv) exportados pela BingX.",
          howToTitle: "Como exportar o histórico da BingX (Standard Futures)",
          howSteps: [
            "Entre na BingX.",
            "Acesse o histórico de Standard Futures / Futuros Standard.",
            "Abra a exportação de dados do histórico correspondente.",
            "Selecione o período desejado.",
            "Gere/Exporte o arquivo (Standard_Futures.csv).",
            "Faça o download do CSV.",
            "Envie o CSV original aqui no GENI, sem editar ou converter.",
          ],
        },
        en: {
          hintExchange: "Only .csv Standard Futures files (Standard_Futures.csv) exported by BingX.",
          howToTitle: "How to export your BingX history (Standard Futures)",
          howSteps: [
            "Log in to BingX.",
            "Open the Standard Futures history.",
            "Open the data export for the matching history.",
            "Pick the desired period.",
            "Generate/Export the file (Standard_Futures.csv).",
            "Download the CSV.",
            "Upload the original CSV here in GENI, without editing or converting it.",
          ],
        },
      },
    },
    {
      id: "mexc", name: "MEXC", enabled: true, format: "XLSX",
      hist: { ptBR: "Até 1 ano por arquivo (Order History), conforme disponibilidade da exchange.", en: "Up to 1 year per file (Order History), subject to exchange availability." },
      guide: {
        ptBR: {
          hintExchange: "Somente arquivos .xlsx exportados oficialmente pela MEXC Futures (Order History).",
          howToTitle: "Como exportar o histórico da MEXC Futures",
          howSteps: [
            "Entre na MEXC.",
            "Vá em Orders.",
            "Acesse Futures Orders.",
            "Abra \"Order & Trade History\".",
            "Clique em \"Export Order History\".",
            "Selecione o período desejado (até 365 dias por exportação; a MEXC disponibiliza os últimos ~18 meses).",
            "Exporte o arquivo no formato Excel/XLSX.",
            "Quando o arquivo ficar disponível, faça o download.",
            "Volte ao GENI e envie o arquivo aqui na área de Import Exchange Data. Use o XLSX original da MEXC, sem editar ou converter.",
          ],
        },
        en: {
          hintExchange: "Only .xlsx files officially exported by MEXC Futures (Order History).",
          howToTitle: "How to export your MEXC Futures history",
          howSteps: [
            "Log in to MEXC.",
            "Go to Orders.",
            "Open Futures Orders.",
            "Open \"Order & Trade History\".",
            "Click \"Export Order History\".",
            "Select the desired period (up to 365 days per export; MEXC provides the last ~18 months).",
            "Export the file in Excel/XLSX format.",
            "Once the file is ready, download it.",
            "Back in GENI, upload the file here in Import Exchange Data. Use MEXC's original XLSX, without editing or converting it.",
          ],
        },
      },
    },
    {
      id: "lbank", name: "LBANK", enabled: true, format: "XLSX",
      hist: { ptBR: "Período disponibilizado pela LBank.", en: "Period made available by LBank." },
      guide: {
        ptBR: {
          hintExchange: "Somente arquivos .xlsx exportados oficialmente pela LBank Futures.",
          howToTitle: "Como exportar o histórico da LBank Futures",
          howSteps: [
            "Acesse a LBank Futures e abra Orders / Histórico de transações (Transaction History).",
            "Selecione o período desejado (a LBank permite exportar por intervalo de datas).",
            "Clique em Export / Exportar e escolha o formato XLSX.",
            "Faça o upload do arquivo .xlsx exportado aqui no GENI.",
          ],
        },
        en: {
          hintExchange: "Only .xlsx files officially exported by LBank Futures.",
          howToTitle: "How to export your LBank Futures history",
          howSteps: [
            "Go to LBank Futures and open Orders / Transaction History.",
            "Select the desired period (LBank lets you export by date range).",
            "Click Export and choose the XLSX format.",
            "Upload the exported .xlsx file here in GENI.",
          ],
        },
      },
    },
  ];

  const current = EXCHANGES.find((e) => e.id === selectedExchange);
  const acceptExt = ["binance", "bitget", "bingx"].includes(selectedExchange) ? ".csv" : ".xlsx";
  const uploadHintText = t.uploadHint.replace(".xlsx", acceptExt);
  const guide = (current && current.guide && current.guide[lang]) || {
    hintExchange: lang === "ptBR"
      ? `Somente arquivos ${acceptExt} exportados oficialmente pela ${current ? current.name : ""}.`
      : `Only ${acceptExt} files officially exported by ${current ? current.name : ""}.`,
    howToTitle: t.howToTitle,
    howSteps: t.howSteps,
  };

  const fetchTrades = async () => {
    setLoadingTrades(true);
    try {
      const res = await api.get(`/exchange-trades/recognized-trades?exchange=lbank&source=import`);
      setTrades(res.data || []);
    } catch (e) {
      setTrades([]);
    } finally {
      setLoadingTrades(false);
    }
  };

  useEffect(() => { fetchTrades(); }, []);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(acceptExt)) {
      toast.error(acceptExt === ".csv" ? (lang === "ptBR" ? "Envie um arquivo .csv válido." : "Please upload a valid .csv file.") : t.onlyXlsx);
      return;
    }
    setUploading(true);
    setImportSummary(null);
    try {
      const form = new FormData();
      form.append("exchange", selectedExchange);
      form.append("file", file);
      const res = await api.post("/exchange-import/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportSummary(res.data);
      toast.success(t.importCompleted);
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === "ptBR" ? "Falha ao importar arquivo." : "Failed to import file."));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    if (!current?.enabled) return;
    const f = e.dataTransfer.files?.[0];
    doUpload(f);
  };

  const openCompleteModal = async (trade) => {
    setCompleteTrade(trade);
    setCSetup(""); setCTimeframe(""); setCHandSize("");
    try {
      const s = await api.get(`/setups`);
      setSetups(Array.isArray(s.data) ? s.data : (s.data?.setups || []));
    } catch (e) { setSetups([]); }
  };

  const submitCompleteTrade = async () => {
    if (!cSetup || !cTimeframe || !cHandSize || parseFloat(cHandSize) <= 0) {
      toast.error(t.fillFields);
      return;
    }
    setCompleting(true);
    try {
      await api.post(`/exchange-trades/trades/${completeTrade.id}/complete`, {
        setup_id: cSetup, timeframe: cTimeframe, hand_size: parseFloat(cHandSize),
      });
      toast.success(t.completed);
      setCompleteTrade(null);
      fetchTrades();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to complete trade");
    } finally {
      setCompleting(false);
    }
  };

  const formatDateTime = (iso) => {
    try {
      return new Date(iso).toLocaleString(language === "ptBR" ? "pt-BR" : "en-US", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch (e) { return iso; }
  };

  const scrollToTrades = () => {
    tradesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white py-12 px-4 md:px-8 w-full max-w-[1600px] mx-auto" data-testid="import-exchange-view">
      {/* Header */}
      <div className="mb-10">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-3 uppercase bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
          {t.title}
        </h1>
        <p className="text-white/60 text-lg max-w-3xl">{t.subtitle}</p>
      </div>

      {/* Exchange selector cards */}
      <div className="mb-8">
        <label className="text-xs font-bold text-white/40 uppercase tracking-wider mb-3 block">{t.selectExchange}</label>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {EXCHANGES.map((ex) => {
            const active = selectedExchange === ex.id;
            return (
              <button
                key={ex.id}
                type="button"
                onClick={() => { setSelectedExchange(ex.id); setImportSummary(null); }}
                className={`text-left border rounded-sm p-4 transition-all ${
                  active
                    ? "bg-[#00E676]/5 border-[#00E676]/30 ring-1 ring-[#00E676]/40"
                    : "bg-white/[0.01] border-white/5 hover:border-white/15"
                } ${!ex.enabled ? "opacity-80" : ""}`}
                data-testid={`import-exchange-card-${ex.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <ExchangeLogo exchange={ex.id} className="w-5 h-5" />
                  <span className="font-bold text-white tracking-wide text-sm">{ex.name}</span>
                </div>
                {ex.enabled ? (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-[#00E676]/10 text-[#00E676] rounded uppercase">
                    {language === "ptBR" ? "Disponível" : "Available"}
                  </span>
                ) : (
                  <span className="text-[10px] font-bold px-1.5 py-0.5 bg-white/5 text-white/40 rounded uppercase">
                    {language === "ptBR" ? "Em breve" : "Coming soon"}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected exchange detail + upload */}
      <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 mb-10">
        <div className="flex items-center justify-between mb-6 border-b border-white/5 pb-5 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <ExchangeLogo exchange={current.id} className="w-7 h-7" />
            <span className="text-2xl font-black text-white uppercase tracking-wider">{current.name}</span>
          </div>
          {current.enabled && current.guide && (
            <Dialog>
              <DialogTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-indigo-500/25 bg-indigo-500/[0.06] text-indigo-300 hover:bg-indigo-500/[0.12] text-sm font-semibold transition-all"
                  data-testid="import-how-to-export-button"
                >
                  <Info size={16} weight="duotone" />
                  {t.howToExport}
                </button>
              </DialogTrigger>
              <DialogContent className="bg-[#0F0F0F] border-white/10 text-white max-w-lg p-6" data-testid="import-how-to-export-modal">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2.5 text-white uppercase tracking-wider text-sm pr-6">
                    <Info size={20} weight="duotone" className="text-indigo-400" />
                    {guide.howToTitle}
                  </DialogTitle>
                </DialogHeader>
                <ol className="space-y-2.5 mt-2">
                  {guide.howSteps.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/70">
                      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-indigo-500/15 text-indigo-300 text-[11px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ol>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Meta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="bg-white/[0.02] border border-white/5 rounded-sm p-4">
            <p className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-1">{t.formatAccepted}</p>
            <p className="text-sm text-white font-semibold flex items-center gap-2"><FileXls size={16} weight="duotone" className="text-[#00E676]" />{current.format}</p>
          </div>
          <div className="bg-white/[0.02] border border-white/5 rounded-sm p-4">
            <p className="text-[11px] font-bold text-white/40 uppercase tracking-wider mb-1">{t.history}</p>
            <p className="text-sm text-white/80 flex items-center gap-2"><ClockCounterClockwise size={16} weight="duotone" className="text-white/40" />{current.hist[language === "ptBR" ? "ptBR" : "en"]}</p>
          </div>
        </div>

        {current.enabled ? (
          <>
            {/* Upload area */}
            <div
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => !uploading && fileInputRef.current?.click()}
              className={`cursor-pointer rounded-sm border-2 border-dashed transition-all p-10 flex flex-col items-center justify-center text-center ${
                dragActive ? "border-[#00E676]/60 bg-[#00E676]/5" : "border-white/10 bg-white/[0.01] hover:border-white/25"
              }`}
              data-testid="import-upload-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept={acceptExt}
                className="hidden"
                onChange={(e) => doUpload(e.target.files?.[0])}
                data-testid="import-file-input"
              />
              {uploading ? (
                <GeniLoader size="md" text={t.uploading} inline={false} className="!h-auto py-2" />
              ) : (
                <>
                  <UploadSimple size={40} weight="duotone" className="text-white/40 mb-4" />
                  <p className="text-sm text-white/70 font-semibold mb-1">{uploadHintText}</p>
                  <p className="text-xs text-white/35">{guide.hintExchange}</p>
                </>
              )}
            </div>

            {/* Import summary */}
            {importSummary && (
              <div className="mt-6 bg-[#00E676]/[0.04] border border-[#00E676]/20 rounded-sm p-6" data-testid="import-summary">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle size={22} weight="fill" className="text-[#00E676]" />
                  <h3 className="text-lg font-black text-white uppercase tracking-wider">{t.importCompleted}</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <SummaryStat label={t.execImported} value={importSummary.imported} testid="summary-imported" />
                  <SummaryStat label={t.dupIgnored} value={importSummary.duplicates} testid="summary-duplicates" />
                  <SummaryStat label={t.invalidRows} value={importSummary.invalid_rows} testid="summary-invalid" />
                  <SummaryStat label={t.tradesRecognized} value={importSummary.trades_recognized} testid="summary-recognized" />
                  <SummaryStat label={t.tradesPending} value={importSummary.trades_pending_completion} testid="summary-pending" />
                </div>
                <button
                  type="button"
                  onClick={() => navigate("/exchange-trades")}
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all"
                  data-testid="import-view-trades-cta"
                >
                  {t.viewTrades}
                  <ArrowRight size={16} weight="bold" />
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="py-12 flex flex-col items-center justify-center text-center bg-white/[0.01] border border-dashed border-white/5 rounded px-6" data-testid="import-coming-soon">
            <ArrowsClockwise className="text-white/20 mb-4 animate-spin" size={40} style={{ animationDuration: "6s" }} />
            <h4 className="text-lg font-bold text-white mb-2">{t.comingSoon}</h4>
          </div>
        )}
      </div>

      {/* Após importar, a fila de conclusão é a página global Pending Trades (sem duplicar aqui) */}
      <div className="bg-[#0F0F0F] border border-white/5 rounded-sm p-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4" data-testid="import-view-pending-cta-card">
        <div className="flex items-center gap-3">
          <ChartBar size={22} weight="duotone" className="text-[#00E676]" />
          <div>
            <h2 className="text-lg font-black text-white uppercase tracking-wide">{t.tradesToComplete}</h2>
            <p className="text-white/45 text-sm mt-0.5">{t.pendingHint}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate("/exchange-trades")}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#00E676] hover:bg-[#00c867] text-black rounded-sm text-sm font-bold transition-all whitespace-nowrap"
          data-testid="import-view-pending-trades"
        >
          {t.viewTrades}
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}

const SummaryStat = ({ label, value, testid }) => (
  <div className="bg-white/[0.02] border border-white/5 rounded-sm p-3" data-testid={testid}>
    <p className="text-[10px] font-bold text-white/40 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-2xl font-black text-white">{value ?? 0}</p>
  </div>
);
